import React, { useCallback, useMemo } from 'react';
import cn from 'classnames';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { userRoleSelector, userSelector } from 'src/redux/selectors';
import { signOutUser } from 'src/redux/actions/authActions';
import { signOutUserHelper, clearAuthToken } from 'src/helpers/authHelpers';
import { MainLayout, GreenBoxButton } from 'src/components';
import logo from 'src/assets/images/logo-no-title.png';
import { header } from 'src/assets/locales/en/common.json';
import { PAGES } from 'src/constants/pages';
import styles from './HomePage.module.scss';
import { ACCOUNT_ROLES } from 'src/constants/globalConstants';
import { getCompanyWorkspacesHook } from 'src/hooks/getCompanyWorkspacesHook';
import { getCurrentWorkspaceFromLocalStorage } from 'src/utils/comon';

const HomePage = (): JSX.Element => {
  const { push } = useHistory();
  const dispatch = useDispatch();
  const user = useSelector(userSelector);
  const userRole = useSelector(userRoleSelector);

  const { companyWorkspaces, isLoading } = getCompanyWorkspacesHook();

  const selectedWorkspace = useMemo(
    () => getCurrentWorkspaceFromLocalStorage(user?.attributes?.email),
    [user?.attributes?.email]
  );

  const handleSignInAsDifferentUser = async () => {
    await signOutUserHelper();
    clearAuthToken();
    dispatch(signOutUser());
    push(PAGES.SIGN_IN);
  };

  const onClick = useCallback(() => {
    const hasCompanyWorkspaces = !!companyWorkspaces?.length;

    const redirectPages = {
      [ACCOUNT_ROLES.USER]: selectedWorkspace ? PAGES.DASHBOARD : PAGES.WORKSPACES_CHOOSE,
      [ACCOUNT_ROLES.COMPANY]: hasCompanyWorkspaces ? PAGES.WORKSPACES_MANAGEMENT : PAGES.WORKSPACES_CREATE,
      [ACCOUNT_ROLES.MULTIUSER]: PAGES.CHOOSE_ACCOUNT_TYPE
    };

    push(redirectPages[userRole] || PAGES.HOME);
  }, [companyWorkspaces?.length, push, selectedWorkspace, userRole]);

  const isDisabled = userRole === ACCOUNT_ROLES.COMPANY && isLoading;

  return (
    <MainLayout isPrivate={false}>
      {user ? (
        <div className={styles.signInAsPreviousWrapper}>
          <GreenBoxButton
            onClick={onClick}
            btnClass={styles.signInAsPreviousBtn}
            disabled={isDisabled}
          >{`Sign in as ${user?.attributes?.email}`}</GreenBoxButton>

          <button className={styles.signInAsDifferentBtn} type="button" onClick={handleSignInAsDifferentUser}>
            Sign in as a different user?
          </button>
        </div>
      ) : (
        <div className={styles.homeContent}>
          <div className={styles.logoBlock}>
            <div className={styles.logoWrapper}>
              <img src={logo} alt={header.logoAlt} className={cn(styles.logo, 'img-fluid')} />
            </div>
            <h1 className={styles.logoTitle}>{header.logoTitle}</h1>
          </div>
          <h2 className={styles.title}>Welcome To Our Platform</h2>
          <div className={styles.btnBlock}>
            <GreenBoxButton onClick={() => push(PAGES.SIGN_IN)} />
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default HomePage;
